import { Add, Visibility, ListAlt, Refresh } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import useDebounce from '../../../hooks/useDebounce';
import {
  fetchCustomerData,
  fetchDeletedCustomer,
} from '../../../modules/admin';
import { customerDataHeaders, deletedCustomersDataHeaders } from '../../../static-data/projects';
import ActionButton from '../../../ui/primitives/ActionButton';
import SearchBar from '../../../ui/primitives/SearchBar';
import TableComponent from '../../../ui/data/table/TableComponent';
import CustomerTableActions from './CustomerTableActions';
import CustomerFormModal from './CustomerFormModal';

const CustomerDataTable = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [perPage, setPerPage] = useState(10);
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const fetchCustomerList = async () => {
    setLoading(true);
    try {
      let response = {};
      if (showDeleted) {
        response = await fetchDeletedCustomer(
          page,
          perPage,
          debouncedSearchQuery
        );
      } else {
        response = await fetchCustomerData(page, perPage, debouncedSearchQuery);
      }

      setCustomers(response?.data || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerList();
  }, [debouncedSearchQuery, page, showDeleted, perPage]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setLoading(true);
    setSearchValue(value);
    setPage(1);
  };

  const handleShowDeleted = () => {
    setShowDeleted(!showDeleted);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Customers
      </Typography>

      {/* Single row: search on the left, actions + refresh on the right */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <SearchBar
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          clearSearch={() => handleSearch('')}
          placeholder="Search Customers"
        />

        <Stack direction="row" spacing={1.5} alignItems="center">
          {!showDeleted && (
            <ActionButton
              label="Add Customer"
              icon={<Add />}
              intent="primary"
              onClick={() => setShowAddCustomerModal(true)}
              tooltip="Add New Customer"
            />
          )}
          <ActionButton
            label={showDeleted ? 'Show Existing Customers' : 'Show Deleted Customers'}
            icon={showDeleted ? <Visibility /> : <ListAlt />}
            intent="secondary"
            onClick={handleShowDeleted}
            tooltip={`Click to show ${showDeleted ? 'existing' : 'deleted'} customers`}
          />
          <ActionButton
            icon={<Refresh />}
            intent="secondary"
            onClick={fetchCustomerList}
            tooltip="Click to refresh the list"
          />
        </Stack>
      </Stack>

      <TableComponent
        data={customers || []}
        headerTabs={showDeleted ? deletedCustomersDataHeaders : customerDataHeaders}
        page={page}
        handlePageChange={handlePageChange}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        defaultSort="customer"
        isLoading={loading}
        striped
        totalCount={totalCount}
        showPages
        tablekey="customers"
        showCount
        showRowCount
        setPerPage={handlePageSizeChange}
        action={(row) => {
          if (showDeleted) return null;
          return (
            <CustomerTableActions
              row={row}
              fetchCustomerList={fetchCustomerList}
              latestExport={row.latest_export || null}
              updateExportRequest={fetchCustomerList}
            />
          );
        }}
      />

      <CustomerFormModal
        open={showAddCustomerModal}
        setOpen={setShowAddCustomerModal}
        fetchCustomerList={fetchCustomerList}
      />
    </Box>
  );
};

export default CustomerDataTable;
